/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import { TableColumn } from '@g17eco/types/universalTracker';
import { InputColumn, RowDataInfo } from '@g17eco/types/questionInterfaces';
import { BaseInputProps } from '../InputProps';
import { getDefaultTableUnitOrNumberScaleCode } from '@utils/universalTrackerValue';

export function updateRowData<T extends { id: number | string } = RowDataInfo>(data: T[], row: Partial<T>) {
  let found = false;
  const newData = data.map(r => {
    if (r.id === row.id) {
      found = true;
      return { ...r, ...row }
    }
    return r;
  })
  return found ? newData : data;
}

/**
 * we add unit / number scale to formRow regardless of visibility to display default it values if needed
 * before we submit, we must clear these fields if they are not answered
 */
export const transformRowsOnAddOrUpdate = (formRow: InputColumn[]) => {
  return formRow.reduce((acc, row) => {
    if (row.value) {
      acc.push({ ...row });
      return acc;
    }
    acc.push({ code: row.code });
    return acc;
  }, [] as InputColumn[]);
};

const invalidValues: (undefined | string | null | number | string[])[] = [undefined, '', null,];

export const isDisabled = (columns: TableColumn[], data: InputColumn[]) => {
  if (data.length === 0 || data.every(({ value }) => invalidValues.includes(value))) {
    return true;
  }

  return data.some(({ value, code }) => {
    const colMetaData = columns.find(col => col.code === code);
    if (value !== undefined && !Array.isArray(value)) {
      if (colMetaData?.validation?.min !== undefined) {
        if (Number(value) < colMetaData.validation.min) {
          return true;
        }
      }
      if (colMetaData?.validation?.max !== undefined) {
        if (Number(value) > colMetaData.validation.max) {
          return true;
        }
      }
      return false;
    }

    if (colMetaData?.validation?.required) {
      if (Array.isArray(value)) {
        if (value.some(v => invalidValues.includes(v))) {
          return true;
        }
      } else if (invalidValues.includes(value)) {
        return true;
      }
    }
    return false;
  });
}

export const getSingleRowTableAddon = (
  params: Pick<BaseInputProps, 'addons'> & { columnCode: string }
) => {
  const { addons, columnCode } = params;
  const beforeAddon = (addons?.before ?? []).find((a) => a.rowIndex === 0 && a.code === columnCode);
  const afterAddon = (addons?.after ?? []).find((a) => a.rowIndex === 0 && a.code === columnCode);

  return { beforeAddon, afterAddon };
};

export const getOverriddenTableColumns = (columns: TableColumn[], unitConfig: BaseInputProps['unitConfig']) => {
  return columns.map((column) => {
    const col: InputColumn = { code: column.code };
    const unit = getDefaultTableUnitOrNumberScaleCode({ column, unitConfig, key: 'unit' });
    const numberScale = getDefaultTableUnitOrNumberScaleCode({ column, unitConfig, key: 'numberScale' });
    if (unit) {
      col.unit = unit;
    }
    if (numberScale) {
      col.numberScale = numberScale;
    }
    return col;
  });
};

export const hasAtLeastOneRequiredColumn = (columns: TableColumn[]) => {
  return columns.some(col => col.validation?.required);
}
